import { ARTIGOS_BLOG, artigoPorSlug } from "./blog/artigos.js";

export const ORIGEM = "https://alugando.com";
export const IMAGEM_SOCIAL = `${ORIGEM}/icone-512.png`;

export interface SeoPagina {
  arquivo: string;
  titulo: string;
  descricao: string;
  descricaoSocial?: string;
  imagemSocial?: string;
  imagemSocialLargura?: number;
  imagemSocialAltura?: number;
  imagemSocialAlt?: string;
  tipoOpenGraph?: "website" | "article";
  canonical: string;
  robots: string;
}

export type CaminhoPublico = "/" | "/calculadora-imposto-aluguel" | "/termos"
  | "/privacidade" | "/blog" | `/blog/${string}`;

const PAGINAS_FIXAS: Record<Exclude<CaminhoPublico, `/blog/${string}`>, SeoPagina> = {
  "/": {
    arquivo: "index.html",
    titulo: "alugando.com · Você aluga. O resto é com a gente.",
    descricao: "Você aluga. O resto é com a gente: o alugando.com cobra o locatário, acompanha quem pagou e cuida do imposto do aluguel (carnê-leão) todos os meses, antes que o atraso vire multa. A partir de R$ 34,90/mês no plano anual.",
    descricaoSocial: "Cobrança educada do inquilino, controle de quem pagou e o imposto do aluguel no automático. A partir de R$ 34,90/mês no plano anual, primeiro mês grátis.",
    canonical: `${ORIGEM}/`,
    robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  },
  "/calculadora-imposto-aluguel": {
    arquivo: "calculadora-imposto-aluguel.html",
    titulo: "Calculadora de imposto sobre aluguel 2026 | Alugando.com",
    descricao: "Calcule gratuitamente se há carnê-leão sobre os aluguéis recebidos de pessoas físicas e estime o DARF do mês. Resultado imediato e sem cadastro.",
    canonical: `${ORIGEM}/calculadora-imposto-aluguel`,
    robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  },
  "/termos": {
    arquivo: "termos.html",
    titulo: "Termos de uso | Alugando.com",
    descricao: "Termos de uso do alugando.com.",
    canonical: `${ORIGEM}/termos`,
    robots: "noindex,follow",
  },
  "/privacidade": {
    arquivo: "privacidade.html",
    titulo: "Política de privacidade | Alugando.com",
    descricao: "Política de privacidade e tratamento de dados do alugando.com.",
    canonical: `${ORIGEM}/privacidade`,
    robots: "noindex,follow",
  },
  "/blog": {
    arquivo: "blog/index.html",
    titulo: "Blog sobre aluguel, recebimentos e carnê-leão | Alugando.com",
    descricao: "Guias práticos para locadores organizarem recebimentos, documentos, cobrança e imposto sobre aluguel.",
    canonical: `${ORIGEM}/blog/`,
    robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  },
};

export function paginasSeo(): Array<SeoPagina & { caminho: CaminhoPublico }> {
  const fixas = Object.entries(PAGINAS_FIXAS).map(([caminho, pagina]) => ({
    caminho: caminho as CaminhoPublico,
    ...pagina,
  }));
  const artigos = ARTIGOS_BLOG.map((artigo) => {
    const caminho = `/blog/${artigo.slug}` as CaminhoPublico;
    return {
      caminho,
      arquivo: `blog/${artigo.slug}/index.html`,
      titulo: `${artigo.titulo} | Alugando.com`,
      descricao: artigo.descricao,
      descricaoSocial: artigo.resumo,
      imagemSocial: new URL(artigo.imagem.src, ORIGEM).href,
      imagemSocialLargura: artigo.imagem.largura,
      imagemSocialAltura: artigo.imagem.altura,
      imagemSocialAlt: artigo.imagem.alt,
      tipoOpenGraph: "article" as const,
      canonical: `${ORIGEM}${caminho}/`,
      robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    };
  });
  return [...fixas, ...artigos];
}

export const SEO_PAGINAS = Object.fromEntries(
  paginasSeo().map(({ caminho, ...pagina }) => [caminho, pagina]),
) as Record<CaminhoPublico, SeoPagina>;
export const CAMINHOS_PUBLICOS = paginasSeo().map((pagina) => pagina.caminho);

export function paginaSeo(caminho: CaminhoPublico): SeoPagina {
  const pagina = SEO_PAGINAS[caminho];
  if (!pagina) throw new Error(`Página SEO não cadastrada: ${caminho}`);
  return pagina;
}

const organizacao = {
  "@type": "Organization",
  "@id": `${ORIGEM}/#organization`,
  name: "Alugando.com",
  url: `${ORIGEM}/`,
  logo: {
    "@type": "ImageObject",
    url: IMAGEM_SOCIAL,
    width: 512,
    height: 512,
  },
  legalName: "Tiago Silva Loureiro Tecnologia da Informacao LTDA",
  taxID: "40.838.857/0001-08",
  email: "suporte@alugando.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Rua Parnaíba, 585, Minaslândia",
    postalCode: "31812-280",
    addressLocality: "Belo Horizonte",
    addressRegion: "MG",
    addressCountry: "BR",
  },
};

const site = {
  "@type": "WebSite",
  "@id": `${ORIGEM}/#website`,
  url: `${ORIGEM}/`,
  name: "Alugando.com",
  alternateName: "alugando.com",
  inLanguage: "pt-BR",
  publisher: { "@id": `${ORIGEM}/#organization` },
};

export function dadosEstruturados(caminho: CaminhoPublico): object {
  const pagina = paginaSeo(caminho);
  const webPage = {
    "@type": "WebPage",
    "@id": `${pagina.canonical}#webpage`,
    url: pagina.canonical,
    name: pagina.titulo,
    description: pagina.descricao,
    inLanguage: "pt-BR",
    isPartOf: { "@id": `${ORIGEM}/#website` },
    about: { "@id": `${ORIGEM}/#organization` },
  };

  if (caminho === "/") {
    return {
      "@context": "https://schema.org",
      "@graph": [
        organizacao,
        site,
        webPage,
        {
          "@type": ["SoftwareApplication", "WebApplication"],
          "@id": `${ORIGEM}/#software`,
          name: "Alugando.com",
          url: `${ORIGEM}/`,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          inLanguage: "pt-BR",
          description: "Gestão do aluguel para o locador pessoa física: cobrança educada do inquilino por email, controle de quem pagou mês a mês, recibos, e o carnê-leão calculado com DARF oficial emitido automaticamente.",
          image: IMAGEM_SOCIAL,
          publisher: { "@id": `${ORIGEM}/#organization` },
          offers: {
            "@type": "Offer",
            price: "418.80",
            priceCurrency: "BRL",
            availability: "https://schema.org/InStock",
            url: `${ORIGEM}/#precos`,
            description: "Primeiro mês grátis; plano anual de R$ 418,80, equivalente a R$ 34,90 por mês. Mensal sem fidelidade a partir de R$ 49,90.",
          },
          review: {
            "@type": "Review",
            author: { "@type": "Person", name: "Mirian Silva" },
            reviewRating: {
              "@type": "Rating",
              ratingValue: "4.9",
              bestRating: "5",
            },
            reviewBody: "Pela primeira vez eu sei exatamente onde estou com cada imóvel.",
          },
        },
      ],
    };
  }

  if (caminho === "/blog") {
    return {
      "@context": "https://schema.org",
      "@graph": [organizacao, site, webPage, {
        "@type": "Blog",
        "@id": `${pagina.canonical}#blog`,
        url: pagina.canonical,
        name: "Blog do Alugando.com",
        inLanguage: "pt-BR",
        publisher: { "@id": `${ORIGEM}/#organization` },
        blogPost: ARTIGOS_BLOG.map((artigo) => ({
          "@id": `${ORIGEM}/blog/${artigo.slug}/#article`,
        })),
      }],
    };
  }

  if (caminho.startsWith("/blog/")) {
    const artigo = artigoPorSlug(caminho.slice("/blog/".length));
    if (!artigo) return { "@context": "https://schema.org", "@graph": [organizacao, site, webPage] };
    return {
      "@context": "https://schema.org",
      "@graph": [organizacao, site, webPage, {
        "@type": "BlogPosting",
        "@id": `${pagina.canonical}#article`,
        headline: artigo.titulo,
        description: artigo.descricao,
        image: new URL(artigo.imagem.src, ORIGEM).href,
        datePublished: artigo.publicadoEm,
        dateModified: artigo.revisadoEm,
        inLanguage: "pt-BR",
        author: { "@type": "Person", name: artigo.autor.nome },
        reviewedBy: { "@type": "Person", name: artigo.revisor.nome },
        publisher: { "@id": `${ORIGEM}/#organization` },
        mainEntityOfPage: { "@id": `${pagina.canonical}#webpage` },
        keywords: artigo.palavrasChave.join(", "),
      }, {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Início", item: `${ORIGEM}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${ORIGEM}/blog/` },
          { "@type": "ListItem", position: 3, name: artigo.titulo, item: pagina.canonical },
        ],
      }],
    };
  }

  return {
    "@context": "https://schema.org",
    "@graph": [organizacao, site, webPage],
  };
}
